/**
 * A request to the facilitator that is still waiting for an answer.
 *
 * The waiting screen can sit for minutes while the facilitator gets to the
 * list, and a phone put in a pocket for that long is a tab the operating system
 * may discard. Coming back would otherwise land on the join screen, with the
 * request still in the facilitator's queue and nothing on this phone able to
 * collect the answer. So the one thing that cannot be re-derived, the token
 * that collects it, is written down the moment the request is made.
 *
 * The token is a credential of a narrow kind: once approved, it signs in one
 * browser, once. It lives in the same store as the seat token (`api.ts`) on a
 * device that already holds that, and it is cleared the moment it is used or
 * abandoned.
 */

const STORAGE_KEY = "tq.pendingAccess";

/**
 * How long a waiting request is worth restoring. Long enough to cover a
 * facilitator who gets to the list at the next break; short enough that a
 * shared laptop does not resurrect yesterday's request for the next person.
 */
const WINDOW_MS = 4 * 60 * 60 * 1000;

export interface PendingAccess {
  token: string;
  email: string;
  name: string;
  session: { code: string; title: string };
  /** Epoch milliseconds. */
  expiresAt: number;
}

export function readPendingAccess(): PendingAccess | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let record: Partial<PendingAccess> | null;
  try {
    record = JSON.parse(raw) as Partial<PendingAccess> | null;
  } catch {
    clearPendingAccess();
    return null;
  }

  if (
    !record ||
    typeof record.token !== "string" ||
    typeof record.email !== "string" ||
    typeof record.name !== "string" ||
    typeof record.expiresAt !== "number" ||
    typeof record.session?.code !== "string" ||
    typeof record.session?.title !== "string"
  ) {
    clearPendingAccess();
    return null;
  }
  if (record.expiresAt <= Date.now()) {
    clearPendingAccess();
    return null;
  }
  return {
    token: record.token,
    email: record.email,
    name: record.name,
    session: { code: record.session.code, title: record.session.title },
    expiresAt: record.expiresAt,
  };
}

export function writePendingAccess(record: Omit<PendingAccess, "expiresAt">): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...record, expiresAt: Date.now() + WINDOW_MS }),
    );
  } catch {
    // Non-fatal: the request stands; it just will not survive a reload.
  }
}

export function clearPendingAccess(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignored */
  }
}
